import { Badge, Col, Divider, Empty, Flex, Modal, Space } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import StatusTag from "components/elements/TagCustom/StatusTag";

const MyTeamLeaveCalendarModal = ({
    isOpen = false,
    onClose = () => { },
    selectedDate = "",
    employeeList = [],
    setIsDetailModalOpen = () => { },
    request = []
}) => {
    const { t, i18n } = useTranslation();
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    useEffect(() => {
        if (isOpen && employeeList?.length > 0) {
            setSelectedEmployee(employeeList[0]);
        } else {
            setSelectedEmployee(null);
        }
    }, [isOpen, employeeList]);

    const renderEmployeeItem = (employee) => {
        const isActive = selectedEmployee && String(selectedEmployee?.id) === String(employee?.id);
        const employeeName = i18n.language === 'en' && employee?.fullnameEn !== "null null"
            ? employee?.fullnameEn
            : employee?.fullname;

        const hasLeave = employee?.txnLeaveRequest && employee?.txnLeaveRequest.length > 0;
        const hasOffsite = employee?.txnOffsiteWorkRequest && employee?.txnOffsiteWorkRequest.length > 0;

        const badgeColor = hasLeave ? "#FF9F2D" : "#0085FF";
        const backgroundColor = hasLeave ? "#FFF5E9" : "#E6F3FF";

        return (
            <div
                key={String(employee?.id)}
                onClick={() => setSelectedEmployee(employee)}
                style={{
                    cursor: "pointer",
                    marginBottom: "8px",
                    backgroundColor: isActive ? "#EEF2FF" : "#F8FAFC",
                    border: isActive ? "1px solid var(--button-color)" : "1px solid #ffff",
                    transition: "all 0.15s ease-in-out",
                    borderRadius: "6px"
                }}
            >
                <Badge
                    color={badgeColor}
                    text={employeeName}
                    style={{
                        backgroundColor: backgroundColor,
                        borderRadius: "5px",
                        padding: "2px 8px",
                        color: badgeColor,
                        width: "100%"
                    }}
                />
            </div>
        );
    };

    const calculateDuration = (doc) => {
        if (!doc) return '-';

        if (doc?.durationType === "ALLDAY") {
            return `${doc?.totalDuration} ${t("unit.day")}`;
        } else if (doc?.durationType === "HOUR") {
            const totalMinutes = doc?.totalDuration || 0;
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;

            if (hours > 0 && minutes > 0) {
                return `${hours} ${t("unit.hours")} ${minutes} ${t("unit.minute")}`;
            } else if (hours > 0) {
                return `${hours} ${t("unit.hour")}`;
            } else if (minutes > 0) {
                return `${minutes} ${t("unit.minute")}`;
            }
        }
        return '-';
    };

    const renderDetailSection = () => {
        if (!selectedEmployee) {
            return (
                <Flex justify="center" align="center" style={{ height: "100%" }}>
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("common.no_data")} />
                </Flex>
            );
        }

        const employeeName = i18n.language === 'en' && selectedEmployee?.fullnameEn !== "null null"
            ? selectedEmployee?.fullnameEn
            : selectedEmployee?.fullname;

        const allRequests = [];

        if (selectedEmployee?.txnLeaveRequest && selectedEmployee?.txnLeaveRequest?.length > 0) {
            selectedEmployee?.txnLeaveRequest?.forEach(leave => {
                allRequests.push({
                    type: 'leave',
                    doc: leave,
                    badgeText: i18n.language === 'en'
                        ? leave?.leaveType?.leaveTypeNameEn
                        : leave?.leaveType?.leaveTypeName,
                    badgeColor: "#FF9F2D",
                    backgroundColor: "#FFF5E9"
                });
            });
        }

        if (selectedEmployee?.txnOffsiteWorkRequest && selectedEmployee?.txnOffsiteWorkRequest?.length > 0) {
            selectedEmployee?.txnOffsiteWorkRequest?.forEach(offsite => {
                allRequests.push({
                    type: 'offsite',
                    doc: offsite,
                    badgeText: i18n.language === 'en'
                        ? offsite?.workTypeId?.leaveTypeNameEn
                        : offsite?.workTypeId?.leaveTypeName,
                    badgeColor: "#0085FF",
                    backgroundColor: "#E6F3FF"
                });
            });
        }

        if (allRequests?.length === 0) {
            return (
                <Flex justify="center" align="center" style={{ height: "100%" }}>
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("common.no_data")} />
                </Flex>
            );
        }

        const eventDate = selectedDate && dayjs(selectedDate).isValid()
            ? (i18n.language === "en"
                ? dayjs(selectedDate).format("DD/MM/YYYY")
                : dayjs(selectedDate).format("DD/MM/BBBB"))
            : '-';

        return (
            <div style={{ maxHeight: '55vh', overflowY: 'auto', width: '100%' }}>
                <Space direction="vertical" style={{ width: "100%" }} size={16}>
                    <span style={{ color: "#344054", fontSize: "18px", fontWeight: 500 }}>
                        {employeeName}
                    </span>

                    {allRequests.map((request, index) => (
                        <div key={index}>
                            <Space direction="vertical" style={{ width: "100%" }} size={8}>
                                <Badge
                                    color={request.badgeColor}
                                    text={request.badgeText}
                                    style={{
                                        backgroundColor: request.backgroundColor,
                                        borderRadius: "5px",
                                        padding: "2px 8px",
                                        color: request.badgeColor,
                                        width: "100%"
                                    }}
                                />
                                <Flex justify="space-between">
                                    <span style={{ color: "#667085", borderLeft: "2px solid #757575", paddingLeft: "8px" }}>
                                        {eventDate}
                                    </span>
                                    <span>{calculateDuration(request?.doc)}</span>
                                </Flex>

                                <Flex justify="space-between" align="end">
                                    <Flex vertical>
                                        <span style={{ fontSize: "12px", color: "#A0AEC0" }}>
                                            {t("expense.expense-detail.request-id")}
                                        </span>
                                        <span>{request?.doc?.sequence || '-'}</span>
                                    </Flex>

                                    <div style={{ height: "max-content" }}>
                                        <StatusTag value={request?.doc?.approveStatus || '-'} />
                                    </div>
                                </Flex>
                            </Space>

                            {index < allRequests?.length - 1 && (
                                <Divider style={{ border: "0.5px solid #ddd", margin: "12px 0px" }} />
                            )}
                        </div>
                    ))}
                </Space>
            </div>
        );
    };

    return (
        <Modal
            title={
                <span style={{ color: "#344054", fontWeight: "bold", fontSize: "18px" }}>
                    {selectedDate && dayjs(selectedDate).isValid()
                        ? (i18n.language === "en"
                            ? dayjs(selectedDate)?.format("DD/MM/YYYY")
                            : dayjs(selectedDate)?.format("DD/MM/BBBB"))
                        : '-'
                    }
                </span>
            }
            open={isOpen}
            onCancel={onClose}
            footer={null}
            width="50vw"
        >
            <Flex style={{ width: "100%" }} justify="space-between">
                <Col span={10} style={{ maxHeight: "60vh", overflowY: "auto", paddingRight: "8px", scrollbarWidth: "thin" }}>
                    {employeeList?.length > 0
                        ? employeeList?.map(renderEmployeeItem)
                        : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("common.no_data")} />
                    }
                </Col>
                <Divider type="vertical" style={{ border: "0.5px solid #ddd", height: "auto" }} />
                <Col span={13}>
                    {renderDetailSection()}
                </Col>
            </Flex>
        </Modal>
    );
};

export default MyTeamLeaveCalendarModal;